import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import PageContainer from "../../components/layout/PageContainer";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import ServiceBriefingAssets from "../../components/service-briefings/ServiceBriefingAssets";
import ServiceBriefingPageShell from "../../components/service-briefings/ServiceBriefingPageShell";
import {
  SERVICE_BRIEFING_STATUSES,
  STATUS_COLOR,
  euro,
  formatDay,
  isOverdue,
  kindLabel,
  statusLabel,
} from "../../components/service-briefings/serviceBriefingLabels";
import { deleteServiceBriefingFile } from "../../firebase/serviceBriefingStorage";
import { useUserRole } from "../../hooks/useUserRole";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  useDeleteServiceBriefingMutation,
  useGetServiceBriefingHistoryQuery,
  useGetServiceBriefingQuery,
  useUpdateServiceBriefingStatusMutation,
} from "../../store/apis/serviceBriefingApi";
import {
  ServiceBriefingHistoryEntry,
  ServiceBriefingStatus,
} from "../../types/serviceBriefing";

const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const ViewServiceBriefing: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id = "" } = useParams<{ id: string }>();
  const { isAdmin, isStrategy } = useUserRole();
  const canDelete = isAdmin || isStrategy;

  const { data, isLoading, isError } = useGetServiceBriefingQuery(id);
  const briefing = data?.data ?? null;
  const { data: historyData } = useGetServiceBriefingHistoryQuery(id);
  const history = historyData?.data ?? [];

  const [updateStatus, { isLoading: isUpdatingStatus }] =
    useUpdateServiceBriefingStatusMutation();
  const [deleteBriefing, { isLoading: isDeleting }] =
    useDeleteServiceBriefingMutation();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const section = (
    title: string,
    children: React.ReactNode,
    action?: React.ReactNode
  ) => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="subtitle1">{title}</Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );

  const handleStatus = async (status: ServiceBriefingStatus) => {
    try {
      await updateStatus({ id, status }).unwrap();
    } catch (error: unknown) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        t("serviceBriefings.view.statusFailed", "No se pudo cambiar el estado");
      dispatch(setToastMessage({ message, severity: "error" }));
    }
  };

  const handleDelete = async () => {
    // Las rutas se guardan antes: tras el borrado la ficha ya no existe.
    const storagePaths = (briefing?.assets ?? [])
      .map((asset) => asset.storagePath)
      .filter((path): path is string => Boolean(path));
    try {
      await deleteBriefing(id).unwrap();
      // El backend borra las filas; los ficheros, que subió el frontend, se borran aquí.
      // Si alguno falla solo queda huérfano en el bucket: no se avisa.
      await Promise.allSettled(storagePaths.map(deleteServiceBriefingFile));
      dispatch(
        setToastMessage({
          message: t("serviceBriefings.view.deleted", "Briefing borrado"),
          severity: "success",
        })
      );
      navigate("/service-briefings");
    } catch {
      dispatch(
        setToastMessage({
          message: t(
            "serviceBriefings.view.deleteFailed",
            "No se pudo borrar el briefing"
          ),
          severity: "error",
        })
      );
    }
  };

  const historyLabel = (entry: ServiceBriefingHistoryEntry): string => {
    switch (entry.action) {
      case "CREATE":
        return t("serviceBriefings.history.CREATE", "Creado");
      case "STATUS": {
        const next = entry.changes?.status?.new as
          | ServiceBriefingStatus
          | undefined;
        return next
          ? t("serviceBriefings.history.STATUS", "Estado → {{status}}", {
              status: statusLabel(t, next),
            })
          : t("serviceBriefings.history.STATUS_GENERIC", "Cambio de estado");
      }
      case "ASSET_ADD":
        return t("serviceBriefings.history.ASSET_ADD", "Asset añadido");
      case "ASSET_DELETE":
        return t("serviceBriefings.history.ASSET_DELETE", "Asset quitado");
      default:
        return t("serviceBriefings.history.UPDATE", "Editado: {{fields}}", {
          fields: Object.keys(entry.changes ?? {}).join(", "),
        });
    }
  };

  if (isLoading) {
    return (
      <ServiceBriefingPageShell>
        <Box textAlign="center" py={6}>
          <CircularProgress />
        </Box>
      </ServiceBriefingPageShell>
    );
  }

  if (isError || !briefing) {
    return (
      <ServiceBriefingPageShell>
        <PageContainer>
          <Alert severity="error">
            {t(
              "serviceBriefings.view.notFound",
              "No se ha encontrado el briefing"
            )}
          </Alert>
        </PageContainer>
      </ServiceBriefingPageShell>
    );
  }

  const overdue = isOverdue(briefing.deliveryDate, briefing.status);
  const overBudget =
    briefing.budget > 0 && briefing.totals.totalPrice > briefing.budget;

  const info = (label: string, value: React.ReactNode) => (
    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {value}
      </Typography>
    </Grid>
  );

  return (
    <ServiceBriefingPageShell>
      <PageContainer sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Stack direction="row" gap={1} alignItems="center" mb={0.5}>
              <Chip size="small" label={kindLabel(t, briefing.kind)} />
              <Chip
                size="small"
                color={STATUS_COLOR[briefing.status]}
                label={statusLabel(t, briefing.status)}
              />
            </Stack>
            <Typography variant="h5" sx={{ wordBreak: "break-word" }}>
              {briefing.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {briefing.companyName}
            </Typography>
          </Box>
          <Stack direction="row" gap={1} alignItems="flex-start">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={briefing.status}
                onChange={(e) =>
                  handleStatus(e.target.value as ServiceBriefingStatus)
                }
                disabled={isUpdatingStatus}
                inputProps={{
                  "aria-label": t("serviceBriefings.fields.status", "Estado"),
                }}
              >
                {SERVICE_BRIEFING_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {statusLabel(t, s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/service-briefings/${id}/edit`)}
            >
              {t("serviceBriefings.view.edit", "Editar")}
            </Button>
            {canDelete && (
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmDelete(true)}
              >
                {t("serviceBriefings.view.delete", "Borrar")}
              </Button>
            )}
          </Stack>
        </Stack>

        {overdue && (
          <Alert severity="error">
            {t(
              "serviceBriefings.view.overdue",
              "La fecha de entrega ({{date}}) ya ha pasado.",
              { date: formatDay(briefing.deliveryDate) }
            )}
          </Alert>
        )}

        {section(
          t("serviceBriefings.form.general", "Datos generales"),
          <Grid container spacing={2}>
            {info(
              t("serviceBriefings.fields.campaignName", "Nombre de campaña"),
              briefing.campaignName
            )}
            {info(
              t("serviceBriefings.fields.brandOrProduct", "Marca o producto"),
              briefing.brandOrProduct || "—"
            )}
            {info(
              t("serviceBriefings.fields.period", "Mes"),
              `${MONTH_NAMES[briefing.month - 1]} ${briefing.year}`
            )}
            {info(
              t("serviceBriefings.fields.budget", "Presupuesto (€)"),
              euro.format(briefing.budget)
            )}
            {info(
              t("serviceBriefings.fields.requestDate", "Fecha de solicitud"),
              formatDay(briefing.requestDate)
            )}
            {info(
              t("serviceBriefings.fields.deliveryDate", "Fecha de entrega"),
              <Box
                component="span"
                sx={{ color: overdue ? "error.main" : undefined }}
              >
                {formatDay(briefing.deliveryDate)}
              </Box>
            )}
            {info(
              t("serviceBriefings.fields.assignee", "Asignado a"),
              briefing.assignee?.name ?? "—"
            )}
            {info(
              t("serviceBriefings.fields.createdBy", "Creado por"),
              briefing.createdBy?.name ?? "—"
            )}
            {info(
              t("serviceBriefings.fields.oc", "OC"),
              briefing.oc ? (
                <Link
                  component={RouterLink}
                  to={`/purchase-orders/${briefing.oc.id}/edit`}
                >
                  {briefing.oc.ocNumber}
                </Link>
              ) : (
                "—"
              )
            )}
            {briefing.notes && (
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary">
                  {t("serviceBriefings.fields.notes", "Notas")}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {briefing.notes}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}

        {section(
          t("serviceBriefings.form.lines", "Líneas"),
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t("serviceBriefings.lines.concept", "Concepto")}
                  </TableCell>
                  <TableCell align="right">
                    {t("serviceBriefings.lines.quantity", "Cantidad")}
                  </TableCell>
                  <TableCell align="right">
                    {t("serviceBriefings.lines.unitCost", "Coste unitario")}
                  </TableCell>
                  <TableCell align="right">
                    {t("serviceBriefings.lines.unitPrice", "Precio unitario")}
                  </TableCell>
                  <TableCell align="right">
                    {t("serviceBriefings.lines.totalCost", "Coste")}
                  </TableCell>
                  <TableCell align="right">
                    {t("serviceBriefings.lines.totalPrice", "Precio")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {briefing.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>{line.concept}</TableCell>
                    <TableCell align="right">{line.quantity}</TableCell>
                    <TableCell align="right">
                      {euro.format(line.unitCost)}
                    </TableCell>
                    <TableCell align="right">
                      {euro.format(line.unitPrice)}
                    </TableCell>
                    <TableCell align="right">
                      {euro.format(line.totalCost)}
                    </TableCell>
                    <TableCell align="right">
                      {euro.format(line.totalPrice)}
                    </TableCell>
                  </TableRow>
                ))}
                {briefing.lines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          "serviceBriefings.lines.empty",
                          "Sin líneas todavía."
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={4} align="right">
                    <strong>
                      {t("serviceBriefings.lines.total", "Total")}
                    </strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{euro.format(briefing.totals.totalCost)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{euro.format(briefing.totals.totalPrice)}</strong>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={5} align="right">
                    {t("serviceBriefings.lines.margin", "Margen")}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color:
                        briefing.totals.margin < 0 ? "error.main" : undefined,
                    }}
                  >
                    {euro.format(briefing.totals.margin)} (
                    {briefing.totals.marginPct.toFixed(1)} %)
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
            {overBudget && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t(
                  "serviceBriefings.view.overBudget",
                  "El precio total ({{price}}) supera el presupuesto ({{budget}}).",
                  {
                    price: euro.format(briefing.totals.totalPrice),
                    budget: euro.format(briefing.budget),
                  }
                )}
              </Alert>
            )}
          </>
        )}

        {section(
          t("serviceBriefings.assets.title", "Assets"),
          <ServiceBriefingAssets
            serviceBriefingId={briefing.id}
            assets={briefing.assets}
          />
        )}

        {section(
          t("serviceBriefings.history.title", "Histórico"),
          history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("serviceBriefings.history.empty", "Sin cambios registrados.")}
            </Typography>
          ) : (
            <Stack gap={1}>
              {history.map((entry) => (
                <Typography key={entry.id} variant="body2">
                  <Box component="span" color="text.secondary">
                    {new Date(entry.createdAt).toLocaleString("es-ES")}
                    {entry.user ? ` · ${entry.user.name}` : ""}
                  </Box>
                  {" — "}
                  {historyLabel(entry)}
                </Typography>
              ))}
            </Stack>
          )
        )}

        <ConfirmationModal
          open={confirmDelete}
          title={t("serviceBriefings.view.deleteTitle", "Borrar briefing")}
          message={t(
            "serviceBriefings.view.deleteMessage",
            "Se borrará «{{name}}» con sus líneas, assets e histórico. No se puede deshacer.",
            { name: briefing.name }
          )}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
          isLoading={isDeleting}
        />
      </PageContainer>
    </ServiceBriefingPageShell>
  );
};

export default ViewServiceBriefing;
